import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation } from 'react-router-dom'
import SEO from './SEO'
import { trackLead, trackEvent } from '../utils/analytics'
import './../styles/ContactForm.css'

const ContactForm = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [emailCopied, setEmailCopied] = useState(false)
  const consent = watch('consent', false)

  const handleCopyEmail = async (e) => {
    e.preventDefault()
    const email = 'info@qatechltd.co.uk'
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(email)
      } else {
        // Fallback for older browsers / non-https contexts
        const ta = document.createElement('textarea')
        ta.value = email
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setEmailCopied(true)
      trackEvent('email_copy', { email_location: 'contact_form' })
      window.setTimeout(() => setEmailCopied(false), 2000)
    } catch (err) {
      // Silent fail — user can still click the mailto link
      console.warn('Could not copy email:', err)
    }
  }

  const onSubmit = async (data) => {
    setIsLoading(true)
    setErrorMessage('')
    
    // Prepare form data for Web3Forms
    const formData = new FormData()
    formData.append('access_key', '2ce12c19-a4fa-44f1-8872-7c914f851007')
    formData.append('name', `${data.firstName} ${data.lastName || ''}`.trim())
    formData.append('email', data.email)
    formData.append('company', data.companyName || '')
    formData.append('message', data.message)
    formData.append('subject', 'New Contact Form Submission - QA Tech')
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      })
      
      const result = await response.json()
      
      if (result.success) {
        setIsSubmitted(true)
        setErrorMessage('')
        reset()
        // Conversion event — primary KPI for this site.
        trackLead({
          form_location:
            typeof window !== 'undefined' ? window.location.pathname : '',
          has_company: Boolean(data.companyName),
        })
      } else {
        throw new Error(result.message || 'Form submission failed')
      }
    } catch (error) {
      setIsSubmitted(false)
      setErrorMessage('Failed to send the message. Please try again later.')
      trackEvent('contact_form_error', {
        error_message: String(error?.message || error || 'unknown'),
      })
      console.error('Form submission error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // ContactForm renders both as the /contact page AND as an embedded form on
  // other pages. Only emit SEO when it's the standalone page so we don't
  // overwrite the host page's meta.
  const location = useLocation()
  const isContactPage = location.pathname === '/contact'

  return (
    <div className="container" id="contact-form">
      {isContactPage && (
        <SEO
          path="/contact"
          title="Contact QA Tech"
          description="Get in touch with QA Tech for software testing, app and web development, technical resourcing or marketing services. We typically reply within 24 hours."
          keywords="contact QA Tech, hire QA consultancy, software testing enquiry, app development enquiry, QA Tech email"
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            url: 'https://qatechltd.com/contact',
            mainEntity: {
              '@type': 'Organization',
              name: 'QA Tech',
              email: 'info@qatechltd.co.uk',
              url: 'https://qatechltd.com',
            },
          }}
        />
      )}
      <div className="message-container">
        <span className="contact-eyebrow">Let&rsquo;s talk</span>
        <h1 className="heading">Ready to make change happen?</h1>
        <p className="contact-lede">
          Tell us about your business and what you&rsquo;re trying to grow.
          We&rsquo;ll come back with a clear, no-obligation plan and a fair
          quote &mdash; usually within a working day.
        </p>

        <ul className="contact-promises" aria-label="What to expect">
          <li>
            <span className="contact-promises__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            </span>
            <span><strong>Reply within 24 hours</strong> on weekdays</span>
          </li>
          <li>
            <span className="contact-promises__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            </span>
            <span><strong>No-obligation</strong> first conversation</span>
          </li>
          <li>
            <span className="contact-promises__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </span>
            <span><strong>Honest quotes</strong> &mdash; no surprises</span>
          </li>
        </ul>

        <div className="contact-details">
          <p className="contact-details__row">
            <span className="contact-details__label">Email</span>
            <span className="contact-details__value">
              <a
                href="mailto:info@qatechltd.co.uk"
                onClick={() => trackEvent('email_click', { email_location: 'contact_form' })}
              >
                info@qatechltd.co.uk
              </a>
              <button
                type="button"
                className={`contact-details__copy${emailCopied ? ' is-copied' : ''}`}
                onClick={handleCopyEmail}
                aria-label={emailCopied ? 'Email copied to clipboard' : 'Copy email to clipboard'}
                title={emailCopied ? 'Copied!' : 'Copy email'}
              >
                {emailCopied ? (
                  /* tick — confirmation */
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  /* two-rectangle copy icon */
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="11" height="11" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
                <span className="contact-details__copy-text">
                  {emailCopied ? 'Copied' : 'Copy'}
                </span>
              </button>
            </span>
          </p>

          <p className="contact-details__row">
            <span className="contact-details__label">LinkedIn</span>
            <a
              className="contact-details__link"
              href="https://www.linkedin.com/company/qa-tech-consultancy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('outbound_click', {
                link_url: 'https://www.linkedin.com/company/qa-tech-consultancy',
                link_label: 'LinkedIn',
                link_location: 'contact_form',
              })}
            >
              QA Tech Consultancy
              <svg
                className="contact-details__external"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          </p>

          <p className="contact-details__row">
            <span className="contact-details__label">Based in</span>
            <span>London, United Kingdom</span>
          </p>
        </div>
      </div>
      <div className="form-container">
        <form className="contact-form" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <div className="form-group half-width">
              <label htmlFor="firstName">
                First name<span className="asterisk">*</span>
              </label>
              <input id="firstName" {...register('firstName', { required: true })} style={{ maxWidth: '85%' }} />
              {errors.firstName && <span className="error-message">First name is required</span>}
            </div>
            <div className="form-group half-width">
              <label htmlFor="lastName">Last name</label>
              <input id="lastName" {...register('lastName')} style={{ maxWidth: '85%' }} />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="email">
              Email<span className="asterisk">*</span>
            </label>
            <input id="email" {...register('email', { required: true })} />
            {errors.email && <span className="error-message">Email is required</span>}
          </div>
          <div className="form-group">
            <label htmlFor="companyName">Company Name (optional)</label>
            <input id="companyName" {...register('companyName')} />
          </div>
          <div className="form-group">
            <label htmlFor="message">
              Your Message<span className="asterisk">*</span>
            </label>
            <textarea
              id="message"
              {...register('message', { required: true, maxLength: 500 })}
              maxLength="500"
            ></textarea>
            {errors.message && (
              <span className="error-message">Message is required and should be less than 500 characters</span>
            )}
          </div>
          <div className="form-group consent-group">
            <input type="checkbox" {...register('consent', { required: true })} />
            <label>
              I consent to my personal information being processed in accordance with the{' '}
              <Link to="/privacy-policy">privacy policy</Link>.
            </label>
          </div>
          {isSubmitted && (
            <div className="success-message">
              <strong>Message Sent Successfully!</strong>
              <br />
              Thank you for contacting QA Tech. We'll get back to you within 24 hours.
            </div>
          )}
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          <button type="submit" disabled={!consent || isLoading} className={`${!consent || isLoading ? 'disabled' : ''} ${isLoading ? 'loading' : ''}`}>
            {isLoading ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ContactForm
